import { getLastProView } from '../pro-web-helper';
import { TrackingHelper } from '../tracking/tracking-helper';
import type { SnowplowContext, TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { calculateViewportPixels } from '@/helpers/ui-helper';

async function openModal(
	component: any,
	componentProps: any = {},
	options: any = {},
	location = '', // For tracking.
	customModalOpenEvent = false,
	additionalContexts: SnowplowContext[] = [],
	onCloseCallback?: Function,
	init?: Function
) {
	await VFMHelper.ensureVfmCss();

	init?.();

	const { id, cssClass, classes, lockScroll, ...restOptions } = options;
	const modalProps = {
		...(id && { id }),
		...(cssClass && { class: cssClass }),
		onWillDismiss: () => {},
		onDidDismiss: () => {},
		...componentProps,
		location,
	};

	let componentName = component?.extendOptions?.name || component?.__name;
	if (!componentName) {
		const dynamicComponent = await component();
		componentName =
			dynamicComponent?.default?.extendOptions?.name ||
			dynamicComponent?.default?.__name ||
			dynamicComponent?.default?.name;
	}

	const trackingInfo: TrackingProviderPropertiesInterface = { action: 'modal_opened' };

	switch (componentName) {
		case 'AccountModal':
			if (location !== '') trackingInfo.property = location;
		case 'SettingsModal':
			trackingInfo.label = 'user_account';
			break;
		case 'MarkAsSeenModal':
			trackingInfo.label = 'tv_show_tracking';
			break;
		case 'MarkAsSeenModalGraphql':
			trackingInfo.label = 'tv_show_tracking';
			break;
		case 'CancelProModal':
			if (location !== '') trackingInfo.property = location;
			trackingInfo.label = 'FreemiumCancellationModal';
			break;
		case 'SearchTitlesToAddModal':
			trackingInfo.label = 'SearchTitlesToAddLists';
			trackingInfo.property = 'lists';
			break;
		case 'AddToListModal':
		case 'CreateCustomListModal':
		case 'RenameListModal':
			trackingInfo.label = componentName;
			trackingInfo.property = 'lists';
			break;
		case 'ConsentModal':
			trackingInfo.nonInteraction = true;
		case 'JwtvTitleRentalModal':
			trackingInfo.property = 'jwtv-rent-modal';
			break;
		default:
			trackingInfo.label = componentName;
			break;
	}

	if (!customModalOpenEvent) {
		TrackingHelper.trackEvent('userinteraction', trackingInfo, additionalContexts);
	}

	const isMobileWeb = calculateViewportPixels() < 768;
	const isMarkAsSeenModal =
		componentName === 'MarkAsSeenModal' ||
		componentName === 'MarkAsSeenModalGraphql' ||
		componentName === 'MarkAsSeenModalContainer';
	const shouldUseSheetModal = isMarkAsSeenModal && isMobileWeb;
	const finalClasses = shouldUseSheetModal ? 'vfm-sheet-modal' : classes;

	const { promise } = VFMHelper.open({
		props: {
			escToClose: true,
			...(finalClasses && { classes: finalClasses }),
			...(lockScroll !== undefined && { lockScroll }),
		},
		onBeforeClosed: modalProps?.onWillDismiss,
		onClosed: () => {
			modalProps?.onDidDismiss();
			onCloseCallback?.();

			if (!customModalOpenEvent) {
				TrackingHelper.trackEvent(
					'userinteraction',
					{
						action: 'modal_closed',
						label: trackingInfo.label,
						property:
							componentName === 'ProModal'
								? getLastProView() || undefined
								: componentName === 'CancelProModal'
								? 'SettingsProSubscription'
								: trackingInfo.property,
						nonInteraction: componentName === 'ConsentModal' ? true : undefined,
					},
					additionalContexts
				);
			}
		},
		slotComponent: component,
		slotProps: modalProps,
		slotId: id,
		...restOptions,
	});

	return promise;
}

export const ModalHelper = {
	openModal,
};
